import React from 'react'

import { useRouter } from 'next/router'

import clsx from 'clsx'
import { useWindowSize } from 'react-use'

import { Link } from '@/components/CustomLink'

import { SidebarItem } from './SidebarItem'

export type SidebarLink = {
	text: string
	link?: {
		href: string
		target?: '_self' | '_blank'
		childLinks?: SidebarLink[]
	}
}

type Props = {
	className?: string
	links?: SidebarLink[]
	contact?: {
		email: string
		text: string
	}
	extraSection?: {
		text: string
		link?: {
			href: string
			target?: '_self' | '_blank'
		}
	}[]
}

export function Sidebar({ className, links, contact, extraSection }: Props) {
	const { asPath, push } = useRouter()
	const isPressCenter = asPath === '/press'
	const { width } = useWindowSize(1440, 900)

	function flattenCategories(links?: SidebarLink[]) {
		return links?.flatMap((category) => {
			const categories = [category]
			if ((category?.link?.childLinks?.length ?? 0) > 0) {
				const childCategoryNames = flattenCategories(category?.link?.childLinks) ?? []
				categories.push(...childCategoryNames)
			}
			return categories
		})
	}

	// Usage
	return (
		<section
			data-testid={width >= 768 ? 'sidebar-desktop' : 'sidebar-mobile'}
			className={clsx(className, 'w-full h-full')}
		>
			{/* Links */}
			<div className="hidden md:grid grid-flow-row">
				{links?.map((link) => (
					<SidebarItem key={link.text} item={link} width={width} />
				))}
			</div>

			<div className="md:hidden">
				<select
					className="w-full border-gray-1000 bg-transparent rounded-sm"
					value={asPath}
					onChange={(event) => push(event.target.value)}
				>
					{flattenCategories(links)?.map((item) => (
						<option key={item.text} value={item?.link?.href}>
							{item.text}
						</option>
					))}
				</select>
			</div>

			{/* Press Inquiries */}
			{contact && !isPressCenter && (
				<div className="grid-flow-row gap-2 mt-16 mb-9 md:mb-0">
					<p className="text-[12px] md:text-[13.6px] font-normal leading-[27px] text-gray-600">
						{contact.text}
					</p>
					<a className="text-bc-blue font-medium text-xs" href={`mailto:${contact.email}`}>
						{contact.email}
					</a>
				</div>
			)}

			{/* Extra Section */}
			{Boolean(extraSection?.length) && (
				<div className="grid-flow-row gap-2 mt-8 lg:mt-4">
					<div>
						{extraSection?.map(({ link, text }) => (
							<Link
								key={text}
								href={link?.href as string}
								className="flex items-center mb-4 text-base font-normal text-gray-500 hover:text-black-100"
							>
								{text}
							</Link>
						))}
					</div>
				</div>
			)}
		</section>
	)
}
